"use client";

import { useState, useEffect } from "react";
import { useUser } from "@/lib/context/user-context";
import { useUsage } from "@/contexts/usage-context";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CreditCard, LogOut, HelpCircle } from "lucide-react";
import Link from "next/link";
import { useI18n } from "@/lib/i18n";
import { UpgradePromptDialog } from "@/components/ui/upgrade-prompt-dialog";
import { HelpSupportDialog } from "@/components/settings/HelpSupportDialog";
import { cn } from "@/lib/utils";

export function UserProfileSection() {
  const { t, lang } = useI18n() as any;
  const { user, logout } = useUser();
  const { usageInfo, loading, refreshUsage } = useUsage();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  useEffect(() => {
    if (user) {
      refreshUsage();
    }
  }, [user, refreshUsage]);

  // 如果加载完成但 planType 为 null，说明数据可能未正确获取，重新刷新
  useEffect(() => {
    if (user && !loading && usageInfo.planType === null) {
      console.log('🔄 UserProfileSection - planType is null, refreshing usage');
      refreshUsage();
    }
  }, [user, loading, usageInfo.planType, refreshUsage]);

  if (!user) {
    return (
      <div className="rounded-lg border border-border bg-background p-6">
        <div className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('savedWords.loginRequiredDesc', 'Please log in to view your profile.')}
          </p>
          <Button asChild>
            <Link href="/login">{t('common.signIn')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Get user initials for avatar fallback
  const getInitials = (name?: string | null, email?: string | null) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };

  const userEmail = user.email || '';
  const userName = user.user_metadata?.full_name || user.user_metadata?.name || '';
  const userAvatar = user.user_metadata?.avatar_url || user.user_metadata?.picture || '';
  const initials = getInitials(userName, userEmail);

  // Calculate remaining transcript minutes quote (same logic as TranscriptSection)
  const transcriptMinutesRemaining = usageInfo?.transcriptMinutes?.remaining ?? null;
  const transcriptMinutesLimit = usageInfo?.transcriptMinutes?.limit ?? null;
  const hasUnlimitedQuota = usageInfo?.isPremium && (transcriptMinutesLimit ?? 0) < 0;
  const shouldShowTranscriptQuota = transcriptMinutesRemaining !== null && !hasUnlimitedQuota;

  return (
    <>
      <div className="rounded-3xl border border-border bg-background overflow-hidden">
        {/* User Info Header */}
        <div className="p-6 pb-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={userAvatar} alt={userName || userEmail} />
              <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xl">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold truncate">{userName || 'User'}</h3>
              <p className="text-sm text-muted-foreground truncate">{userEmail}</p>
            </div>
          </div>
        </div>

        {/* Plan Status */}
        <div className="px-6 pb-6">
          {loading ? (
            <div className="rounded-xl bg-muted/50 border p-4 space-y-2">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-5 w-28 rounded-full" />
              </div>
              <Skeleton className="h-3 w-36" />
            </div>
          ) : usageInfo.planType === 'free' ? (
            <div className="rounded-xl bg-muted/50 border p-4 space-y-2 animate-in fade-in-0 duration-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('user.plan.free')}</span>
                </span>
                <Badge variant="secondary" className="text-xs">
                  {usageInfo.remainingTranscripts}/{usageInfo.transcriptLimit} {t('user.usage.transcriptsLabel')}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {usageInfo.remainingTranscripts > 0
                  ? (
                    lang === 'chinese'
                      ? t('user.usage.remaining').replace('{remaining}', String(usageInfo.remainingTranscripts))
                      : `${usageInfo.remainingTranscripts} transcript${usageInfo.remainingTranscripts > 1 ? 's' : ''} ${t('user.usage.remaining')}`
                  )
                  : t('user.usage.limitReached')}
              </p>
              {/* {shouldShowTranscriptQuota && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('transcript.dialog.remaining', 'Remaining')}: {transcriptMinutesRemaining} {t('common.time.minutes', 'min')}
                  {transcriptMinutesLimit !== null && transcriptMinutesLimit >= 0 && (
                    <span className="text-muted-foreground/70"> / {transcriptMinutesLimit}</span>
                  )}
                </p>
              )} */}
            </div>
          ) : usageInfo.planType === 'basic' ? (
            <div className="rounded-2xl bg-primary/10 border border-primary/20 p-4 space-y-2 animate-in fade-in-0 duration-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">
                  {usageInfo.planName || 'Basic Plan'}
                </span>
                <Badge variant="secondary" className="text-xs bg-primary/15 border-primary/25 text-primary">
                  Basic
                </Badge>
              </div>
              {user?.user_metadata?.subscriptionCancelAtPeriodEnd && user?.user_metadata?.subscriptionEndDate && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg px-2.5 py-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
                  <span>
                    {t('user.plan.expiresOn', 'Expires {date}').replace('{date}', new Date(user.user_metadata.subscriptionEndDate).toLocaleDateString())}
                  </span>
                </div>
              )}
              {shouldShowTranscriptQuota && !user?.user_metadata?.subscriptionCancelAtPeriodEnd && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('transcript.dialog.remaining', 'Remaining')}: {transcriptMinutesRemaining} {t('common.time.minutes', 'min')}
                  {transcriptMinutesLimit !== null && transcriptMinutesLimit >= 0 && (
                    <span className="text-muted-foreground/70"> / {transcriptMinutesLimit}</span>
                  )}
                </p>
              )}
            </div>
          ) : usageInfo.planType === 'basic_lite' ? (
            <div className="rounded-2xl bg-secondary/70 border border-border p-4 space-y-2 animate-in fade-in-0 duration-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">
                  {usageInfo.planName || 'Basic Plan Lite'}
                </span>
                <Badge variant="secondary" className="text-xs bg-primary/10 border-primary/20 text-primary">
                  Content Access
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Access to episodes with available content
              </p>
            </div>
          ) : usageInfo.planType === 'premium' ? (
            <div className="rounded-xl bg-primary p-4 animate-in fade-in-0 duration-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white">{t('user.badge.premiumMember')}</span>
                {transcriptMinutesRemaining !== null && !hasUnlimitedQuota ? (
                  <Badge className="bg-foreground text-white border-0">
                    {transcriptMinutesRemaining} {t('common.time.minutes', 'min')}
                    {transcriptMinutesLimit !== null && transcriptMinutesLimit >= 0 && (
                      <span className="text-white/80"> / {transcriptMinutesLimit}</span>
                    )}
                  </Badge>
                ) : (
                  <Badge className="bg-foreground text-white border-0">
                    <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('user.badge.unlimited')}</span>
                  </Badge>
                )}
              </div>
              {user?.user_metadata?.subscriptionCancelAtPeriodEnd && user?.user_metadata?.subscriptionEndDate && (
                <div className="flex items-center gap-2 text-xs text-white/80 bg-white/10 rounded-lg px-2.5 py-1.5 mt-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  <span>
                    {t('user.plan.expiresOn', 'Expires {date}').replace('{date}', new Date(user.user_metadata.subscriptionEndDate).toLocaleDateString())}
                  </span>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Actions */}
        <div className="border-t border-border divide-y divide-border">
          {/* Upgrade Button - for free, basic, and basic_lite users */}
          {!loading && (usageInfo.planType === 'free' || usageInfo.planType === 'basic' || usageInfo.planType === 'basic_lite') && (
            <Button
              variant="ghost"
              className="w-full justify-start h-auto py-4 px-6 hover:bg-muted/50 rounded-none"
              onClick={() => setShowUpgradeDialog(true)}
            >
              <CreditCard className="mr-3 h-5 w-5 text-muted-foreground" />
              <span className={cn("flex-1 text-left text-base", lang === 'chinese' ? 'font-normal' : '')}>
                {usageInfo.planType === 'basic_lite' || usageInfo.planType === 'basic'
                  ? t('user.actions.upgradeToPremium', 'Upgrade to Premium')
                  : t('user.actions.upgrade')}
              </span>
            </Button>
          )}

          {/* Help & Support */}
          <Button
            variant="ghost"
            className="w-full justify-start h-auto py-4 px-6 hover:bg-muted/50 rounded-none"
            onClick={() => setShowHelpDialog(true)}
          >
              <HelpCircle className="mr-3 h-5 w-5 text-muted-foreground" />
              <span className={cn("flex-1 text-left text-base", lang === 'chinese' ? 'font-normal' : '')}>
                {t('user.actions.help')}
              </span>
          </Button>

          {/* Logout */}
          <Button
            variant="ghost"
            className="w-full justify-start h-auto py-4 px-6 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-none group"
            onClick={logout}
          >
            <LogOut className="mr-3 h-5 w-5 text-red-500 group-hover:text-red-600 transition-colors" />
            <span className={cn("flex-1 text-left text-base text-red-500 group-hover:text-red-600 transition-colors", lang === 'chinese' ? 'font-normal' : '')}>
              {t('user.actions.logout')}
            </span>
          </Button>
        </div>
      </div>

      {/* Upgrade Prompt Dialog */}
      <UpgradePromptDialog
        isOpen={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        errorType="limit_exceeded"
        remainingCount={usageInfo.remainingTranscripts}
        limitCount={usageInfo.transcriptLimit}
        errorMessage={
          usageInfo.remainingTranscripts === 0 
            ? t('user.dialog.limitReached')
            : (
                lang === 'chinese'
                  ? t('user.dialog.remaining').replace('{remaining}', String(usageInfo.remainingTranscripts))
                  : t('user.dialog.remaining').replace('{remaining}', String(usageInfo.remainingTranscripts))
              )
        }
      />

      {/* Help & Support Dialog */}
      <HelpSupportDialog
        isOpen={showHelpDialog}
        onClose={() => setShowHelpDialog(false)}
      />
    </>
  );
}

